import { useState } from 'react';
import { CheckCircle2, Link2, Loader2, ShieldCheck, TriangleAlert } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import { completeSlackAgentLink } from './lib/api';
import { SlackMark } from './SlackAgentIntegrationSection';
import './SlackAgentLinkPage.css';

function LinkCard({ children }) {
  return (
    <main className="slack-link-page">
      <section className="slack-link-card">{children}</section>
    </main>
  );
}

export default function SlackAgentLinkPage() {
  const [searchParams] = useSearchParams();
  const { user, loading, openAuthModal } = useAuthContext();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const token = (searchParams.get('token') || '').trim();

  if (loading) {
    return <LinkCard><Loader2 className="slack-link-spinner" size={24} /><p>Checking your Latted account…</p></LinkCard>;
  }

  if (!user) {
    return (
      <UniversalLayout>
        <LinkCard>
          <div className="slack-link-brand"><SlackMark size={28} /><Link2 size={24} /></div>
          <span className="slack-link-eyebrow">Slack Agent</span>
          <h1>Sign in to link your account</h1>
          <p>Sign in to the Latted account you want to use when creating projects from Slack.</p>
          <button type="button" className="slack-link-primary" onClick={openAuthModal}>Sign in to Latted</button>
          <span className="slack-link-footnote"><ShieldCheck size={14} /> The Slack member is linked only after you confirm.</span>
        </LinkCard>
      </UniversalLayout>
    );
  }

  const submit = async () => {
    if (!token || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await completeSlackAgentLink(token);
      setResult(response);
      window.history.replaceState({}, '', '/settings/integrations/slack-agent/link?linked=success');
    } catch (err) {
      setError('This Slack account-link request could not be completed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <LinkCard>
        {result ? (
          <>
            <CheckCircle2 className="slack-link-success-icon" size={36} />
            <span className="slack-link-eyebrow">Account linked</span>
            <h1>You’re ready to create from Slack</h1>
            <p>Your Latted account is linked to {result.teamName || 'this Slack workspace'}.</p>
            <Link className="slack-link-primary" to="/settings/integrations">View integrations</Link>
          </>
        ) : !token ? (
          <>
            <TriangleAlert className="slack-link-error-icon" size={36} />
            <span className="slack-link-eyebrow">Invalid link</span>
            <h1>This account-link URL is incomplete</h1>
            <p>Return to Slack and request a fresh account-linking prompt.</p>
            <Link className="slack-link-secondary" to="/settings/integrations">Back to integrations</Link>
          </>
        ) : (
          <>
            <div className="slack-link-brand"><SlackMark size={28} /><Link2 size={24} /></div>
            <span className="slack-link-eyebrow">Confirm account</span>
            <h1>Link Slack to Latted</h1>
            <p>Slack project commands will run as <strong>{user.email}</strong> and use this account’s organization access and billing eligibility.</p>
            {error && <div className="slack-link-error"><TriangleAlert size={16} />{error}</div>}
            <button type="button" className="slack-link-primary" onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 size={16} className="slack-link-spinner" /> : <Link2 size={16} />}
              {submitting ? 'Linking account…' : 'Link this account'}
            </button>
            <span className="slack-link-footnote"><ShieldCheck size={14} /> Links expire after 15 minutes and can be used once.</span>
          </>
        )}
      </LinkCard>
    </AuthenticatedLayout>
  );
}
